import React, { useState, useMemo } from 'react';
import { useActivity } from '../App';

const TYPE_CFG = {
  create:  { color: '#22c55e',  bg: 'rgba(34,197,94,0.12)',  icon: '➕', label: 'Created'   },
  update:  { color: '#6366f1',  bg: 'rgba(99,102,241,0.12)', icon: '✏️', label: 'Updated'   },
  delete:  { color: '#ef4444',  bg: 'rgba(239,68,68,0.12)',  icon: '🗑️', label: 'Deleted'   },
  export:  { color: '#f59e0b',  bg: 'rgba(245,158,11,0.12)', icon: '📊', label: 'Exported'  },
  publish: { color: '#06b6d4',  bg: 'rgba(6,182,212,0.12)',  icon: '🌐', label: 'Published' },
  view:    { color: '#94a3b8',  bg: 'rgba(148,163,184,0.1)', icon: '👁️', label: 'Viewed'    },
};

const fmtTime = (ts) => {
  const d    = new Date(ts);
  const diff = Date.now() - d;
  if (diff < 60000)    return 'Just now';
  if (diff < 3600000)  return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const downloadCSV = (data, filename) => {
  const rows = data.map(r => [r.admin, r.action, r.target, r.type, r.timestamp]);
  const csv  = ['Admin,Action,Target,Type,Timestamp', ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const ActivityLogs = () => {
  const { logs } = useActivity();

  const [search,  setSearch]  = useState('');
  const [typeFilter, setType] = useState('all');
  const [page,    setPage]    = useState(0);
  const PER = 15;

  const filtered = useMemo(() => {
    return logs.filter(l => {
      const q = search.toLowerCase();
      const ms = !q || l.action?.toLowerCase().includes(q) || l.target?.toLowerCase().includes(q) || l.admin?.toLowerCase().includes(q);
      const mt = typeFilter === 'all' || l.type === typeFilter;
      return ms && mt;
    });
  }, [logs, search, typeFilter]);

  const totalPages = Math.ceil(filtered.length / PER);
  const paged      = filtered.slice(page * PER, (page + 1) * PER);

  const typeCounts = useMemo(() =>
    Object.keys(TYPE_CFG).reduce((acc, t) => ({
      ...acc, [t]: logs.filter(l => l.type === t).length
    }), {}), [logs]);

  return (
    <div className="animate-in">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>System</span> / <span className="current">Activity Logs</span></div>
          <h1>Activity Logs</h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Real-time audit trail of admin actions this session
          </p>
        </div>
        <button
          onClick={() => downloadCSV(filtered, 'activity_logs.csv')}
          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, background: 'rgba(99,102,241,0.15)', color: 'var(--accent-primary)' }}>
          📥 Export CSV
        </button>
      </div>

      {/* Type Chips */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <button
          onClick={() => { setType('all'); setPage(0); }}
          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, background: typeFilter === 'all' ? 'var(--accent-primary)' : 'var(--bg-tertiary)', color: typeFilter === 'all' ? 'white' : 'var(--text-secondary)' }}>
          All ({logs.length})
        </button>
        {Object.entries(TYPE_CFG).map(([type, cfg]) => (
          <button key={type}
            onClick={() => { setType(typeFilter === type ? 'all' : type); setPage(0); }}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: `1px solid ${typeFilter === type ? cfg.color + '60' : 'var(--border-color)'}`, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, background: typeFilter === type ? cfg.bg : 'var(--bg-card)', color: typeFilter === type ? cfg.color : 'var(--text-secondary)', transition: 'all 0.2s' }}>
            <span>{cfg.icon}</span>
            <span>{cfg.label} ({typeCounts[type] || 0})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <input
          style={{ padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none', flex: 1 }}
          placeholder="🔍 Search action, target, admin..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
        />
        <span style={{ padding: '9px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '0.82rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {filtered.length} records
        </span>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['#', 'Admin', 'Action', 'Target', 'Type', 'Time'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
                    {logs.length === 0
                      ? '📋 No activity yet this session. Actions you take in the admin panel will appear here.'
                      : 'No logs match your filters.'}
                  </td>
                </tr>
              ) : paged.map((log, i) => {
                const cfg = TYPE_CFG[log.type] || TYPE_CFG.view;
                const ini = log.admin?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '??';
                return (
                  <tr key={log.id || i}
                    style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '11px 14px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>{page * PER + i + 1}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', fontWeight: 700, flexShrink: 0 }}>
                          {ini}
                        </div>
                        <span style={{ fontSize: '0.84rem', fontWeight: 500 }}>{log.admin}</span>
                      </div>
                    </td>
                    <td style={{ padding: '11px 14px', fontWeight: 600, fontSize: '0.85rem' }}>{log.action}</td>
                    <td style={{ padding: '11px 14px', fontSize: '0.82rem', color: 'var(--text-secondary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.target}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', background: cfg.bg, color: cfg.color, fontSize: '0.72rem', fontWeight: 600 }}>
                        {cfg.icon} {cfg.label}
                      </span>
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: '0.78rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {fmtTime(log.timestamp)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {page * PER + 1}–{Math.min((page + 1) * PER, filtered.length)} of {filtered.length}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
                style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', opacity: page === 0 ? 0.4 : 1 }}>‹</button>
              <span style={{ padding: '5px 12px', background: 'var(--accent-primary)', color: 'white', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600 }}>
                {page + 1} / {totalPages}
              </span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;
